import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL, API_TOKEN, type ApiResponse } from "@/lib/api";
import {
  KAKAO_JS_KEY_FALLBACK,
  loadKakaoSdk,
  type KakaoCustomOverlay,
  type KakaoLatLng,
  type KakaoMap,
} from "@/lib/kakao";
import type { LatLon, MapMarker } from "@/lib/types";
import { contrastText, validColor } from "@/lib/color";

const DEFAULT_CENTER: LatLon = { lat: 36.3511, lon: 127.385 };

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function extractMarkers(data: unknown): MapMarker[] {
  if (Array.isArray(data)) return data as MapMarker[];
  const rec = asRecord(data);
  if (rec) {
    if (Array.isArray(rec.markers)) return rec.markers as MapMarker[];
    if (Array.isArray(rec.list)) return rec.list as MapMarker[];
  }
  return [];
}

function extractCenter(res: ApiResponse<unknown>): LatLon {
  const rec = asRecord(res.data);
  const centerObj = asRecord(res.center) ?? asRecord(rec?.center);
  const lat = Number(centerObj?.lat ?? res.center_lat ?? rec?.center_lat);
  const lon = Number(centerObj?.lon ?? res.center_lon ?? rec?.center_lon);
  if (Number.isFinite(lat) && Number.isFinite(lon) && lat && lon) {
    return { lat, lon };
  }
  return DEFAULT_CENTER;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function truncate(value: string, max: number): string {
  return value.length > max ? `${value.slice(0, max)}…` : value;
}

export default function BizMiniMap() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<KakaoMap | null>(null);
  const overlaysRef = useRef<KakaoCustomOverlay[]>([]);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;

    async function boot() {
      try {
        const res = await fetch(`${API_BASE_URL}map.php`, {
          headers: { "X-API-TOKEN": API_TOKEN },
        });
        const text = await res.text();
        let json: ApiResponse<unknown>;
        try {
          json = JSON.parse(text) as ApiResponse<unknown>;
        } catch {
          throw new Error("bad response");
        }
        const markers = extractMarkers(json.data);
        const center = extractCenter(json);
        const jsKey =
          (typeof json.js_key === "string" && json.js_key) || KAKAO_JS_KEY_FALLBACK;

        const kakao = await loadKakaoSdk(jsKey);
        if (!active || !containerRef.current || mapRef.current) return;

        const map = new kakao.maps.Map(containerRef.current, {
          center: new kakao.maps.LatLng(center.lat, center.lon),
          level: 8,
        });
        mapRef.current = map;

        const valid = markers.filter((m) => {
          const lat = Number(m.lat);
          const lon = Number(m.lon);
          return Number.isFinite(lat) && Number.isFinite(lon) && lat !== 0 && lon !== 0;
        });

        valid.forEach((m) => {
          const cate = String(m.cate_name || m.cate || "기타").trim();
          const color =
            validColor(String(m.cate_color ?? "")) ||
            validColor(String(m.color ?? "")) ||
            "#17408B";
          const bizName = String(m.biz_name || "사업장").trim();

          const el = document.createElement("div");
          el.className = "biz-marker";
          el.style.setProperty("--cate-color", color);
          el.innerHTML = `
            <div class="biz-marker__body">
              <span class="biz-marker__cate" style="color:${contrastText(color)}">${escapeHtml(cate.slice(0, 2))}</span>
              <span class="biz-marker__name">${escapeHtml(truncate(bizName, 10))}</span>
            </div>
            <span class="biz-marker__tail"></span>
          `;

          const overlay = new kakao.maps.CustomOverlay({
            position: new kakao.maps.LatLng(Number(m.lat), Number(m.lon)),
            content: el,
            xAnchor: 0.5,
            yAnchor: 1,
            zIndex: 1,
            clickable: false,
          });
          overlay.setMap(map);
          overlaysRef.current.push(overlay);
        });

        if (valid.length > 0) {
          const lats = valid.map((m) => Number(m.lat));
          const lons = valid.map((m) => Number(m.lon));
          const sw = new kakao.maps.LatLng(Math.min(...lats), Math.min(...lons));
          const ne = new kakao.maps.LatLng(Math.max(...lats), Math.max(...lons));
          map.setBounds(new kakao.maps.LatLngBounds(sw as KakaoLatLng, ne as KakaoLatLng));
        }

        setReady(true);
      } catch {
        if (active) setFailed(true);
      }
    }

    boot();

    return () => {
      active = false;
      overlaysRef.current.forEach((o) => o.setMap(null));
      overlaysRef.current = [];
      mapRef.current = null;
    };
  }, []);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => navigate("/bizmap")}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate("/bizmap");
        }
      }}
      className="group relative h-56 md:h-[260px] w-full cursor-pointer overflow-hidden bg-background-100"
      aria-label="민들레 비즈맵 미니 지도 (클릭하여 전체 지도 열기)"
    >
      <div ref={containerRef} className="h-full w-full" />

      {/* 지도 준비 전 표시 */}
      {!ready && !failed && (
        <div className="absolute inset-0 flex items-center justify-center bg-background-100">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500" aria-hidden="true"></i>
        </div>
      )}

      {/* 지도 로드 실패 */}
      {failed && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-background-100 px-6 text-center">
          <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-map-2-line text-xl" aria-hidden="true"></i>
          </span>
          <p className="text-sm text-foreground-600">
            지도를 준비하고 있습니다. 클릭하면 전체 지도로 이동합니다.
          </p>
        </div>
      )}

      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-primary-950/45 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
      <div className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-2 text-background-50">
        <span className="w-9 h-9 flex items-center justify-center rounded-full bg-secondary-500 text-primary-950">
          <i className="ri-map-pin-2-fill text-lg" aria-hidden="true"></i>
        </span>
        <span className="rounded-full bg-primary-950/55 px-3 py-1 font-semibold text-sm md:text-base backdrop-blur-sm">
          회원 사업장 지도 열기
        </span>
      </div>
    </div>
  );
}